import React from "react";
import PropTypes from "prop-types";
import { buildScopeTranslate } from "lib/I18n";
import { deriveResultSummary, deriveResultLabels, isUngradedSaqError } from "../resultUtils";
import { ATTEMPT_STATUS } from "../constants";
import { refPropType } from "../utils";
import ScoreProgressBar from "./ScoreProgressBar";
import ResultsAnnouncement from "./ResultsAnnouncement";
import QuestionResultCard from "./QuestionResultCard";

const tResults = buildScopeTranslate("views.content.assessment_framework.assessments.pages.results");

// The answers under the result. Memoized so a parent that re-renders every second (the
// grading error's countdown) does not re-render and re-sanitize every card.
const ReviewAnswers = React.memo(function ReviewAnswers({ responses, status, reevaluating }) {
  return (
    <div className="th-agentforce-assessment__result-review-answers-container" data-testid="results-review-answers">
      <h2 className="th-agentforce-assessment__result-review-answers-heading">
        {tResults("review_answers")}
      </h2>

      <ul className="th-agentforce-assessment__result-review-answers-list">
        {responses.map((response, index) => (
          <QuestionResultCard
            key={response.id || response.knowledge_check_id || index}
            response={response}
            status={reevaluating && isUngradedSaqError(response, status) ? ATTEMPT_STATUS.AWAITING_EVALUATION : status}
          />
        ))}
      </ul>
    </div>
  );
});

ReviewAnswers.propTypes = {
  responses: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  status: PropTypes.string,
  reevaluating: PropTypes.bool,
};

const AssessmentFrameworkResultsScreen = ({
  result = null,
  presentation,
  onRetake,
  onReturnToTrail,
  retaking = false,
  headingRef = null,
  testOut = null,
  onFeedbackClick = null,
  showFeedbackButton = false,
  header = null,
  reevaluating = false,
}) => {
  if (!result) {
    return null;
  }

  const resultSummary = deriveResultSummary(result, presentation);
  const { passed, scorePercent, retakeAllowed } = resultSummary;
  const { heading, summary, subtitle, warning, progressLabel } = deriveResultLabels(
    resultSummary,
    tResults,
    testOut
  );
  const spoken = [heading, summary, subtitle].filter(Boolean).join(". ");
  const announcement = [spoken, warning].filter(Boolean).join(" ");

  const responses = result.responses || result.knowledge_check_results || [];
  const awaitingEvaluation = result.status === ATTEMPT_STATUS.AWAITING_EVALUATION;

  return (
    <div className="th-agentforce-assessment__result">
      {header || (
        <>
          <ResultsAnnouncement message={announcement} />

          <div className="th-agentforce-assessment__result-summary">
            <h1
              ref={headingRef}
              className="th-agentforce-assessment__result-heading"
              tabIndex="-1">
              {heading}
            </h1>
            {!awaitingEvaluation && (
              <ScoreProgressBar
                scorePercent={scorePercent}
                passed={passed}
                ariaLabel={progressLabel}
              />
            )}
          </div>

          <div className="th-agentforce-assessment__result-text">
            {summary && (
              <p className="th-agentforce-assessment__result-primary">
                {summary}
              </p>
            )}
            <p className="th-agentforce-assessment__result-secondary">
              {subtitle}
            </p>
            {warning ? (
              <strong className="th-agentforce-assessment__result-secondary">{warning}</strong>
            ) : null}
          </div>

          {!awaitingEvaluation && (
            <div className="th-agentforce-assessment__result-cta">
              {!passed && retakeAllowed && (
                <tds-button
                  variant="brand"
                  icon-symbol="undo"
                  onClick={onRetake}
                  disabled={retaking}
                  spinner={retaking}
                  aria-live="polite">
                  <span>{tResults("buttons.retake")}</span>
                </tds-button>
              )}
              <tds-button
                variant={passed || !retakeAllowed ? "brand" : "neutral"}
                onClick={onReturnToTrail}>
                <span>{tResults("buttons.return_to_trail")}</span>
              </tds-button>
            </div>
          )}
        </>
      )}

      {responses.length > 0 && (
        <ReviewAnswers responses={responses} status={result.status} reevaluating={reevaluating} />
      )}

      {showFeedbackButton && (
        <div className="th-agentforce-assessment__result-review-answers-footer">
          <p id="feedback-prompt" className="th-agentforce-assessment__result-review-answers-footer-text">
            {tResults("review_answers_footer")}
          </p>
          <button
            type="button"
            aria-describedby="feedback-prompt"
            onClick={onFeedbackClick}
            className="th-agentforce-assessment__result-review-answers-footer-link">
            {tResults("let_us_know")}
          </button>
        </div>
      )}
    </div>
  );
};

AssessmentFrameworkResultsScreen.propTypes = {
  result: PropTypes.shape({
    score: PropTypes.number,
    passed: PropTypes.bool,
    earned_points: PropTypes.number,
    total_points: PropTypes.number,
    responses: PropTypes.arrayOf(
      PropTypes.shape({
        earned_points: PropTypes.number,
      })
    ),
  }),
  presentation: PropTypes.shape({
    passing_score: PropTypes.number,
    max_attempts: PropTypes.number,
    learner_context: PropTypes.shape({
      attempt_count: PropTypes.number,
    }),
  }).isRequired,
  onRetake: PropTypes.func.isRequired,
  onReturnToTrail: PropTypes.func.isRequired,
  retaking: PropTypes.bool,
  headingRef: refPropType,
  testOut: PropTypes.bool,
  onFeedbackClick: PropTypes.func,
  showFeedbackButton: PropTypes.bool,
  header: PropTypes.node,
  reevaluating: PropTypes.bool,
};

export default AssessmentFrameworkResultsScreen;
